import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSearch, FiDownload, FiActivity, FiCalendar, FiCheckCircle } from 'react-icons/fi';
import './AKM.css';

const AKM = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [loading, setLoading] = useState(false);
  const [semesters, setSemesters] = useState([]);
  const [akmList, setAkmList] = useState([]);
  const [studentData, setStudentData] = useState(null);

  // Handle NIM change - fetch available semesters
  const handleNIMChange = async (e) => {
    const nim = e.target.value;
    setSearchQuery(nim);
    setSelectedSemester('');
    setAkmList([]);
    setStudentData(null);

    if (nim.length >= 5) {
      try {
        // Fetch available semesters for this student
        const res = await axios.get(`/api/akm/semesters/${nim}`);
        setSemesters(res.data.data || []);
      } catch (error) {
        console.error('Error fetching semesters:', error);
        setSemesters([]);
      }
    } else {
      setSemesters([]);
    }
  };

  // Handle search AKM
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Masukkan NIM mahasiswa');
      return;
    }

    if (!selectedSemester) {
      toast.error('Pilih semester');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`/api/akm/student/${searchQuery}/${selectedSemester}`);
      
      if (res.data.success) {
        setAkmList(res.data.data.akm || []);
        setStudentData(res.data.data.student || null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal mengambil data AKM');
      setAkmList([]);
      setStudentData(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle print AKM
  const handlePrintAKM = () => {
    if (!searchQuery || !selectedSemester) {
      toast.error('Pilih NIM dan semester terlebih dahulu');
      return;
    }

    const printUrl = `http://localhost:5000/api/akm/print/${searchQuery}/${selectedSemester}`;
    window.open(printUrl, '_blank');
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    const statusMap = {
      '1': 'badge-active',
      '0': 'badge-inactive'
    };
    return statusMap[status] || 'badge-default';
  };

  // Get status label
  const getStatusLabel = (status) => {
    const statusMap = {
      '1': 'Aktif',
      '0': 'Tidak Aktif'
    };
    return statusMap[status] || 'Unknown';
  };

  return (
    <div className="akm-container">
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiActivity className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">AKM Mahasiswa</h1>
              <p className="header-subtitle">Aktivitas Kuliah Mahasiswa per semester</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Form */}
      <div className="search-card">
        <h3>Cari AKM Mahasiswa</h3>
        <div className="search-form">
          <div className="form-group">
            <label>NIM Mahasiswa</label>
            <input
              type="text"
              className="form-control"
              placeholder="Masukkan NIM"
              value={searchQuery}
              onChange={handleNIMChange}
            />
          </div>

          <div className="form-group">
            <label>Semester</label>
            <select
              className="form-control"
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
              disabled={semesters.length === 0}
            >
              <option value="">Pilih Semester</option>
              {semesters.map((sem) => (
                <option key={sem.id_semester} value={sem.id_semester}>
                  {sem.nama_semester || sem.id_semester} ({sem.jumlah_akm} aktivitas)
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button 
              className="btn btn-primary"
              onClick={handleSearch}
              disabled={loading || !searchQuery || !selectedSemester}
            >
              <FiSearch /> {loading ? 'Mencari...' : 'Cari'}
            </button>

            {akmList.length > 0 && (
              <button 
                className="btn btn-success"
                onClick={handlePrintAKM}
              >
                <FiDownload /> Cetak AKM
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Student Info */}
      {studentData && (
        <div className="student-info-card">
          <div className="student-profile">
            <div className="student-avatar">
              {studentData.photo && studentData.photo !== 'default-student.png' ? (
                <img src={`/uploads/${studentData.photo}`} alt={studentData.name} />
              ) : (
                <div className="avatar-placeholder">
                  {studentData.name ? studentData.name.charAt(0).toUpperCase() : 'M'}
                </div>
              )}
            </div>
            <div className="student-details">
              <h3>{studentData.name || '-'}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="label">NIM</span>
                  <span className="value">{studentData.nim}</span>
                </div>
                <div className="info-item">
                  <span className="label">Program Studi</span>
                  <span className="value">{studentData.nama_program_studi || studentData.program_studi || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Status</span>
                  <span className={`badge ${studentData.status === 'Aktif' ? 'badge-active' : 'badge-inactive'}`}>
                    {studentData.status || '-'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Semester</span>
                  <span className="value">{studentData.semester || '-'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Angkatan</span>
                  <span className="value">{studentData.enrollmentYear || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AKM List */}
      {akmList.length > 0 && (
        <div className="akm-card">
          <div className="card-header">
            <h3>
              <FiCalendar /> Aktivitas Kuliah Mahasiswa
            </h3>
            <span className="badge badge-info">{akmList.length} Aktivitas</span>
          </div>

          <div className="akm-table-container">
            <table className="akm-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Nama Semester</th>
                  <th>Status Mahasiswa</th>
                  <th>IPS</th>
                  <th>IPK</th>
                  <th>SKS Semester</th>
                  <th>SKS Total</th>
                  <th>Biaya Kuliah</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {akmList.map((akm, index) => (
                  <tr key={akm._id || index}>
                    <td>{index + 1}</td>
                    <td>{akm.nama_semester || '-'}</td>
                    <td>{akm.nama_status_mahasiswa || '-'}</td>
                    <td className="text-center">
                      <span className="grade-badge">{akm.ips?.toFixed(2) || '0.00'}</span>
                    </td>
                    <td className="text-center">
                      <span className="grade-badge grade-ipk">{akm.ipk?.toFixed(2) || '0.00'}</span>
                    </td>
                    <td className="text-center">{akm.sks_semester || 0}</td>
                    <td className="text-center">{akm.sks_total || 0}</td>
                    <td className="text-right">
                      {akm.biaya_kuliah_smt 
                        ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(akm.biaya_kuliah_smt)
                        : '-'
                      }
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(akm.status)}`}>
                        {akm.status === '1' && <FiCheckCircle />}
                        {getStatusLabel(akm.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && searchQuery && selectedSemester && akmList.length === 0 && (
        <div className="empty-state">
          <FiActivity className="empty-icon" />
          <h3>Tidak ada data AKM</h3>
          <p>Data AKM untuk mahasiswa dengan NIM {searchQuery} pada semester {selectedSemester} tidak ditemukan.</p>
        </div>
      )}

      {/* Initial State */}
      {!searchQuery && (
        <div className="empty-state">
          <FiActivity className="empty-icon" />
          <h3>Cari AKM Mahasiswa</h3>
          <p>Masukkan NIM mahasiswa dan pilih semester untuk melihat data AKM</p>
        </div>
      )}
    </div>
  );
};

export default AKM;
